// src/pages/PrivacyPolicy.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Home
        </button>

        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: January 12, 2026</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            TaalumaERP ("we", "us", "our") respects your privacy and is committed to protecting your 
            personal data. This Privacy Policy explains how we collect, use, and protect your information.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Name, email, phone number, company name</li>
            <li><strong>Business Data:</strong> Products, customers, orders, inventory data</li>
            <li><strong>Payment Information:</strong> Processed by Stripe (we don't store card details)</li>
            <li><strong>Mobile Money:</strong> Transaction references (not account details)</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Usage Data:</strong> Features used, login times, pages viewed</li>
            <li><strong>Device Information:</strong> Device type, browser, IP address, operating system</li>
            <li><strong>Location Data:</strong> General location based on IP address</li>
            <li><strong>Cookies:</strong> Session cookies for authentication</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Process payments and transactions</li>
            <li>Send service-related communications</li>
            <li>Provide customer support</li>
            <li>Improve and develop new features</li>
            <li>Detect and prevent fraud</li>
            <li>Comply with legal obligations</li>
            <li>Send marketing communications (with your consent)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Data Storage and Security</h2>
          
          <h3>4.1 Storage Location</h3>
          <p>
            Your data is stored on secure servers provided by Railway (cloud infrastructure). 
            Servers are located in data centers compliant with international security standards.
          </p>

          <h3>4.2 Security Measures</h3>
          <ul>
            <li>AES-256 encryption for data at rest</li>
            <li>TLS/SSL encryption for data in transit</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication</li>
            <li>Automated backups every 24 hours</li>
          </ul>

          <h3>4.3 Offline Data</h3>
          <p>
            When using offline mode, data is stored locally on your device and encrypted. 
            Data automatically syncs when you reconnect to the internet.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Data Sharing and Disclosure</h2>
          
          <p><strong>We DO NOT sell your data to third parties.</strong></p>

          <p>We may share your information with:</p>
          
          <h3>5.1 Service Providers</h3>
          <ul>
            <li><strong>Stripe:</strong> Payment processing</li>
            <li><strong>Railway:</strong> Cloud hosting</li>
            <li><strong>Email Services:</strong> Transactional emails</li>
          </ul>

          <h3>5.2 Legal Requirements</h3>
          <p>We may disclose information if required by law or to:</p>
          <ul>
            <li>Comply with legal processes</li>
            <li>Protect our rights and property</li>
            <li>Prevent fraud or security issues</li>
            <li>Protect user safety</li>
          </ul>

          <h3>5.3 Business Transfers</h3>
          <p>
            If TaalumaERP is acquired or merged, your data may be transferred to the new entity. 
            We will notify you before this happens.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Your Rights (GDPR Compliance)</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your data</li>
            <li><strong>Rectification:</strong> Correct inaccurate data</li>
            <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
            <li><strong>Portability:</strong> Export your data in machine-readable format</li>
            <li><strong>Restriction:</strong> Limit how we process your data</li>
            <li><strong>Objection:</strong> Object to processing for marketing purposes</li>
            <li><strong>Withdraw Consent:</strong> Opt out of communications</li>
          </ul>
          <p>
            To exercise these rights, contact us at: <a href="mailto:support@taalumaerp.com">support@taalumaerp.com</a>
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Data Retention</h2>
          <ul>
            <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
            <li><strong>Inactive Accounts:</strong> Data retained for 12 months after last login</li>
            <li><strong>Cancelled Accounts:</strong> Data retained for 30 days for recovery</li>
            <li><strong>Legal Obligations:</strong> Some data retained longer for tax/legal compliance</li>
            <li><strong>Backups:</strong> Backup data may persist up to 90 days</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>8. Cookies and Tracking</h2>
          
          <h3>8.1 Essential Cookies</h3>
          <p>Required for the Service to function (authentication, session management).</p>

          <h3>8.2 Analytics Cookies</h3>
          <p>Help us understand how users interact with the Service (Google Analytics, if enabled).</p>

          <h3>8.3 Cookie Control</h3>
          <p>You can disable cookies in your browser settings, but this may affect functionality.</p>
        </section>

        <section className="legal-section">
          <h2>9. Children's Privacy</h2>
          <p>
            TaalumaERP is not intended for users under 18. We do not knowingly collect information 
            from children. If you believe a child has provided us with data, contact us immediately.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your data may be transferred to and processed in countries outside your residence. 
            We ensure appropriate safeguards are in place to protect your data.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Third-Party Links</h2>
          <p>
            The Service may contain links to third-party websites. We are not responsible for their 
            privacy practices. Please review their policies before providing information.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Changes to Privacy Policy</h2>
          <p>
            We may update this Privacy Policy. We will notify you of material changes via email or 
            in-app notification. Continued use after changes constitutes acceptance.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Data Breach Notification</h2>
          <p>
            In the unlikely event of a data breach affecting your personal information, we will 
            notify you within 72 hours as required by GDPR.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Contact Us</h2>
          <p>
            For questions about this Privacy Policy or to exercise your rights, contact us at:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:support@taalumaerp.com">support@taalumaerp.com</a><br />
            <strong>Address:</strong> Modena, Italy<br />
            <strong>Data Protection Officer:</strong> <a href="mailto:privacy@taalumaerp.com">privacy@taalumaerp.com</a>
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Complaints</h2>
          <p>
            If you're not satisfied with how we handle your data, you have the right to lodge a 
            complaint with your local data protection authority.
          </p>
          <p>
            <strong>Italy:</strong> Garante per la protezione dei dati personali<br />
            Website: <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
